import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { meQueryOptions } from "@/features/auth/api/auth.queries";
import {
  nextRaidQueryOptions,
  raidsQueryOptions,
  setupQueryOptions,
} from "@/features/setup/api/setup.queries";
import { SetupPage } from "@/features/setup/SetupPage";
import { canManageRaids } from "@/shared/lib/roles";

// An invalid ?raid silently falls back to the default (next upcoming) raid.
const searchSchema = z.object({
  raid: z.uuid().optional().catch(undefined),
  view: z.literal("bosses").optional().catch(undefined),
});

export const Route = createFileRoute("/_app/setup")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ raid: search.raid }),
  loader: async ({ context, deps }) => {
    // The next-raid pointer and raid list are always warmed (SetupPage reads
    // them unconditionally). With ?raid in the URL all queries are independent.
    if (deps.raid) {
      await Promise.all([
        context.queryClient.ensureQueryData(nextRaidQueryOptions),
        context.queryClient.ensureQueryData(raidsQueryOptions),
        context.queryClient.ensureQueryData(setupQueryOptions(deps.raid)),
      ]);
      return;
    }
    // Warm the raid list in parallel, but start the (large) setup fetch the moment
    // the next-raid pointer resolves rather than waiting on the list too.
    const raids = context.queryClient.ensureQueryData(raidsQueryOptions);
    const next =
      await context.queryClient.ensureQueryData(nextRaidQueryOptions);
    const setup = next.raidId
      ? context.queryClient.ensureQueryData(setupQueryOptions(next.raidId))
      : undefined;
    await Promise.all([raids, setup]);
  },
  component: SetupRoute,
});

// Passes the manage flag down so setup never imports auth (same pattern as the index route).
function SetupRoute() {
  const { data: me } = useSuspenseQuery(meQueryOptions);
  const { raid, view } = Route.useSearch();
  return (
    <SetupPage
      raidId={raid}
      view={view}
      canEdit={canManageRaids(me.permissionRole)}
    />
  );
}
